(() => {
  'use strict';

  /*
    B&B Heatmap Tracker
    Drop-in storefront tracker for the bb-heatmap Cloudflare Worker.

    Expected Worker endpoint:
      https://bb-heatmap.heatmapbnbfirearms.workers.dev

    Expected Worker payload:
      POST /api/collect
      {
        key,
        sessionId,
        page: { path, title, device, referrer, shop },
        events: [{ type, x, y, vw, vh, dw, dh, target, rage, ts }]
      }
  */

  const DEFAULT_ENDPOINT = 'https://bb-heatmap.heatmapbnbfirearms.workers.dev';

  const cfg = window.BBHeatmapConfig || {};
  const enabled = cfg.enabled !== false;
  const endpointBase = String(cfg.endpoint || DEFAULT_ENDPOINT).trim().replace(/\/$/, '');
  const projectKey = String(cfg.projectKey || 'bnb-prod').trim();
  const sampleRate = Number.isFinite(Number(cfg.sampleRate)) ? Math.max(0, Math.min(1, Number(cfg.sampleRate))) : 1;
  const respectDnt = cfg.respectDnt !== false;

  const overlayQueryKey = 'bb_heatmap';
  const overlayStateKey = 'bb_heatmap_overlay_state';
  const debugOkKey = '__BBHeatmapLastCollectOK';
  const debugErrKey = '__BBHeatmapLastCollectError';
  const orderSentKey = 'bb_heatmap_order_sent';
  const lastPathKey = 'bb_heatmap_last_path';
  const refPathKey = 'bb_heatmap_ref_path';
  const sessionKey = 'bb_heatmap_session_id';

  const ALLOWED_EVENT_TYPES = new Set([
    'click',
    'move',
    'view',
    'scroll',
    'dead_click',
    'gallery_view',
    'variant_change',
    'exit_intent',
    'cart_add',
    'cart_remove',
    'cart_change',
    'time_on_page',
    'filter_change',
    'search',
    'cta_click',
    'form_start',
    'form_submit'
  ]);

  function normalizePath(path) {
    if (!path) return '/';
    let out = String(path).split('#')[0].split('?')[0].trim();
    if (!out) return '/';

    if (/^https?:\/\//i.test(out)) {
      try {
        out = new URL(out).pathname || '/';
      } catch (_err) {
        out = '/';
      }
    }

    if (!out.startsWith('/')) out = '/' + out;
    if (out.length > 1 && out.endsWith('/')) out = out.slice(0, -1);
    return out || '/';
  }

  function clamp01(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    if (n < 0) return 0;
    if (n >= 1) return 0.9999;
    return n;
  }

  function docSize() {
    const d = document.documentElement;
    const b = document.body;
    return {
      width: Math.max(
        d.scrollWidth || 0,
        d.clientWidth || 0,
        b ? b.scrollWidth || 0 : 0,
        b ? b.clientWidth || 0 : 0,
        window.innerWidth || 0
      ),
      height: Math.max(
        d.scrollHeight || 0,
        d.clientHeight || 0,
        b ? b.scrollHeight || 0 : 0,
        b ? b.clientHeight || 0 : 0,
        window.innerHeight || 0
      )
    };
  }

  function sanitizeEndpoint(value) {
    const input = String(value || '').trim();
    if (!input) return '';
    try {
      const url = /^https?:\/\//i.test(input) ? new URL(input) : new URL(`https://${input}`);
      return url.toString().replace(/\/$/, '');
    } catch (_err) {
      return '';
    }
  }

  function escapeForSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value || ''));
    }
    return String(value || '').replace(/["\\]/g, '\\$&');
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function colorFor(norm, opacity) {
    const safeNorm = clamp01(norm);
    const hue = 240 - (240 * safeNorm);
    const alpha = Math.min(0.12 + safeNorm * opacity, 0.92);
    return `hsla(${hue}, 100%, 50%, ${alpha})`;
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }

  function getSessionId() {
    try {
      let id = sessionStorage.getItem(sessionKey);
      if (!id) {
        id = uuid();
        sessionStorage.setItem(sessionKey, id);
      }
      return id;
    } catch (_err) {
      return uuid();
    }
  }

  const sessionId = getSessionId();
  const endpointRoot = sanitizeEndpoint(endpointBase);
  const collectEndpoint = endpointRoot ? `${endpointRoot}/api/collect` : '';

  function pageMeta() {
    return {
      path: normalizePath(location.pathname),
      title: (document.title || '').slice(0, 180),
      device: (window.innerWidth || 0) < 750 ? 'mobile' : 'desktop',
      referrer: document.referrer ? document.referrer.slice(0, 300) : '',
      shop: location.hostname
    };
  }

  function visibleLabel(el) {
    if (!(el instanceof Element)) return '';
    const raw = (
      el.getAttribute('aria-label') ||
      el.textContent ||
      el.getAttribute('name') ||
      el.getAttribute('title') ||
      ''
    ).replace(/\s+/g, ' ').trim();

    return raw.slice(0, 80);
  }

  function nearestActionable(el) {
    if (!(el instanceof Element)) return null;
    return el.closest(
      'a, button, summary, [role="button"], [role="link"], input, select, textarea, label, .button, .product-card, .collection-card, [data-product-handle], [data-handle], [data-card], [data-product-card], [onclick], [tabindex]'
    ) || el;
  }

  function buildSelector(el) {
    if (!(el instanceof Element)) return '';

    const handle = el.getAttribute('data-product-handle') || el.getAttribute('data-handle');
    if (handle) {
      const escaped = escapeForSelector(handle);
      return `[data-product-handle="${escaped}"],[data-handle="${escaped}"]`;
    }

    const href = el.getAttribute('href');
    if (href) {
      const path = normalizePath(href);
      if (path && path !== '/') return `a[href*="${escapeForSelector(path)}"]`;
    }

    const parts = [el.tagName.toLowerCase()];
    if (el.id) parts.push(`#${escapeForSelector(el.id)}`);

    Array.from(el.classList || [])
      .filter(Boolean)
      .slice(0, 3)
      .forEach((cls) => parts.push(`.${escapeForSelector(cls)}`));

    return parts.join('');
  }

  function summarizeTarget(el) {
    const node = nearestActionable(el);
    if (!(node instanceof Element)) return '';

    const selector = buildSelector(node);
    const label = visibleLabel(node);
    const hrefPath = node.getAttribute('href') ? normalizePath(node.getAttribute('href')) : '';

    let context = '';
    const card = node.closest('[data-product-handle], [data-handle], .product-card, .collection-card, [data-product-card]');
    if (card) {
      context = card.getAttribute('data-product-handle') ||
        card.getAttribute('data-handle') ||
        visibleLabel(card).slice(0, 40);
    }

    return [selector, label, hrefPath, context].join('|').slice(0, 180);
  }

  function parseTargetDescriptor(target) {
    const parts = String(target || '').split('|');
    return {
      selector: (parts[0] || '').trim(),
      label: (parts[1] || '').trim(),
      href: normalizePath(parts[2] || ''),
      context: (parts[3] || '').trim()
    };
  }

  function shouldIgnoreTarget(el) {
    if (!(el instanceof Element)) return false;

    return !!el.closest(
      'input[type="password"], input[type="email"], input[type="text"], input[type="tel"], input[type="number"], textarea, [contenteditable="true"], [data-bb-heatmap-ignore]'
    );
  }

  function isSearchTarget(el) {
    if (!(el instanceof Element)) return false;
    return !!el.closest('input[type="search"], [data-search-input], form[action*="/search"] input');
  }

  function isInteractiveTarget(el) {
    if (!(el instanceof Element)) return false;
    const actionable = nearestActionable(el);
    if (!(actionable instanceof Element)) return false;

    return actionable.matches(
      'a, button, summary, [role="button"], [role="link"], input, select, textarea, label, .button, [data-product-card], .product-card, .collection-card, [onclick], [tabindex]'
    );
  }

  function addOverlayStyles() {
    if (document.getElementById('bb-heatmap-overlay-styles')) return;

    const style = document.createElement('style');
    style.id = 'bb-heatmap-overlay-styles';
    style.textContent = `
      .bb-heatmap-overlay-root { position:absolute; inset:0 auto auto 0; width:100%; pointer-events:none; z-index:2147482000; }
      .bb-heatmap-overlay-canvas { display:block; width:100%; height:100%; pointer-events:none; }
      .bb-heatmap-target-layer { position:absolute; inset:0 auto auto 0; width:100%; height:100%; pointer-events:none; z-index:2147482500; }
      .bb-heatmap-target-highlight { position:absolute; border:2px solid rgba(255,255,255,.92); background:rgba(255,255,255,.06); border-radius:1rem; box-shadow:0 1rem 2rem rgba(0,0,0,.18); }
      .bb-heatmap-target-ring { position:absolute; transform:translate(-50%,-50%); border-radius:999px; pointer-events:none; }
      .bb-heatmap-target-pin { position:absolute; transform:translate(-50%,-50%); min-width:2.8rem; min-height:2.8rem; border-radius:999px; display:flex; align-items:center; justify-content:center; font-size:1.05rem; font-weight:800; color:#0f172a; background:#fff; box-shadow:0 .8rem 1.8rem rgba(0,0,0,.28); border:2px solid rgba(255,255,255,.9); padding:.2rem .45rem; white-space:nowrap; }
      .bb-heatmap-target-label { position:absolute; max-width:26rem; background:rgba(15,23,42,.96); color:#fff; border-radius:.95rem; padding:.55rem .75rem; font-size:1.1rem; line-height:1.3; box-shadow:0 .8rem 1.8rem rgba(0,0,0,.28); white-space:normal; text-align:left; border:1px solid rgba(255,255,255,.08); }
      .bb-heatmap-target-label small { display:block; margin-top:.2rem; color:rgba(255,255,255,.72); font-size:.95rem; }
      .bb-heatmap-overlay-ui { position:fixed; right:1.2rem; bottom:1.2rem; width:min(44rem, calc(100vw - 2rem)); max-height:min(88vh, 86rem); overflow:auto; background:rgba(7,12,24,.96); color:#fff; border-radius:1.45rem; padding:1.05rem 1.05rem 1.15rem; box-shadow:0 1.4rem 4rem rgba(0,0,0,.45); z-index:2147483000; pointer-events:auto; font-family:inherit; border:1px solid rgba(255,255,255,.09); backdrop-filter:blur(10px); }
      .bb-heatmap-overlay-ui h3 { margin:0; font-size:1.58rem; color:#fff; }
      .bb-heatmap-overlay-ui p { margin:.2rem 0 0; font-size:1.16rem; line-height:1.48; color:rgba(255,255,255,.86); }
      .bb-heatmap-overlay-head { display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; }
      .bb-heatmap-overlay-headmeta { color:rgba(255,255,255,.7); font-size:1.02rem; margin-top:.2rem; }
      .bb-heatmap-overlay-grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:.8rem; margin-top:.95rem; }
      .bb-heatmap-overlay-card { border-radius:1rem; background:linear-gradient(180deg, rgba(255,255,255,.07), rgba(255,255,255,.045)); border:1px solid rgba(255,255,255,.08); padding:.85rem .95rem; min-height:7.1rem; display:flex; flex-direction:column; justify-content:center; }
      .bb-heatmap-overlay-card strong { display:block; margin-bottom:.22rem; font-size:1.55rem; }
      .bb-heatmap-overlay-controls { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:.8rem; margin-top:.95rem; }
      .bb-heatmap-field { border-radius:1rem; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.08); padding:.7rem .8rem .8rem; }
      .bb-heatmap-field label { display:block; font-size:1rem; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,.68); margin-bottom:.45rem; }
      .bb-heatmap-field select { width:100%; appearance:none; background:rgba(15,23,42,.92); color:#fff; border:1px solid rgba(255,255,255,.12); border-radius:.85rem; padding:.85rem .95rem; min-height:4.4rem; font:inherit; line-height:1.2; }
      .bb-heatmap-field select option { color:#111827; }
      .bb-heatmap-overlay-trail { margin-top:.95rem; border-radius:1rem; background:rgba(255,255,255,.045); border:1px solid rgba(255,255,255,.08); padding:.8rem .9rem; }
      .bb-heatmap-overlay-trail strong { display:block; margin-bottom:.45rem; }
      .bb-heatmap-overlay-trail ol { margin:.2rem 0 0 1.4rem; padding:0; }
      .bb-heatmap-overlay-trail li { margin:.22rem 0; color:rgba(255,255,255,.84); font-size:1.12rem; }
      .bb-heatmap-overlay-hint { margin-top:.8rem; padding:.75rem .9rem; border-radius:1rem; background:rgba(99,102,241,.12); border:1px solid rgba(129,140,248,.22); color:rgba(255,255,255,.88); font-size:1.08rem; line-height:1.45; }
      .bb-heatmap-overlay-actions { display:flex; flex-wrap:wrap; gap:.65rem; margin-top:1rem; }
      .bb-heatmap-overlay-actions button, .bb-heatmap-overlay-actions a { appearance:none; border:0; border-radius:999px; padding:.72rem 1rem; font:inherit; text-decoration:none; background:#fff; color:#111827; cursor:pointer; }
      .bb-heatmap-overlay-actions .bb-heatmap-overlay-close { background:#ef4444; color:#fff; }
      @media (max-width: 749px) {
        .bb-heatmap-overlay-ui { right:.75rem; left:.75rem; width:auto; bottom:.75rem; padding:.9rem; }
        .bb-heatmap-overlay-grid, .bb-heatmap-overlay-controls { grid-template-columns:1fr; }
        .bb-heatmap-target-label { max-width:18rem; }
      }
    `;
    document.head.appendChild(style);
  }

  function removeOverlayParam(urlString) {
    const url = new URL(urlString, window.location.origin);
    url.searchParams.delete(overlayQueryKey);
    return url.pathname + url.search + url.hash;
  }

  function initOverlayMode() {
    const params = new URLSearchParams(window.location.search);
    if (params.get(overlayQueryKey) !== '1') return;

    let state = null;
    try {
      state = JSON.parse(localStorage.getItem(overlayStateKey) || 'null');
    } catch (_err) {
      state = null;
    }

    if (!state || !state.endpoint || !state.projectKey || !state.token) return;
    if (Date.now() - Number(state.createdAt || 0) > 1000 * 60 * 60 * 8) return;
    if (normalizePath(location.pathname) !== normalizePath(state.path || location.pathname)) return;

    const overlayEndpoint = sanitizeEndpoint(state.endpoint);
    if (!overlayEndpoint) return;

    state.kind = state.kind || 'all';
    state.sessionId = state.sessionId || '';

    addOverlayStyles();

    const root = document.createElement('div');
    root.className = 'bb-heatmap-overlay-root';

    const canvas = document.createElement('canvas');
    canvas.className = 'bb-heatmap-overlay-canvas';

    const targetLayer = document.createElement('div');
    targetLayer.className = 'bb-heatmap-target-layer';

    root.appendChild(canvas);
    root.appendChild(targetLayer);
    document.body.appendChild(root);

    const ui = document.createElement('div');
    ui.className = 'bb-heatmap-overlay-ui';
    ui.innerHTML = `
      <div class="bb-heatmap-overlay-head">
        <div>
          <h3>Heatmap overlay</h3>
          <div class="bb-heatmap-overlay-headmeta">${escapeHtml(location.pathname)}</div>
        </div>
      </div>
      <p data-bb-overlay-status>Loading overlay intelligence for ${escapeHtml(location.pathname)}…</p>
      <div class="bb-heatmap-overlay-grid" data-bb-overlay-grid></div>
      <div class="bb-heatmap-overlay-controls">
        <div class="bb-heatmap-field">
          <label for="bb-overlay-kind">Session scope</label>
          <select id="bb-overlay-kind" data-bb-overlay-kind>
            <option value="all">All sessions</option>
            <option value="rage">Rage / confusion sessions</option>
            <option value="high_intent">High-intent sessions</option>
            <option value="cart">Cart-intent sessions</option>
            <option value="dead">Dead-click sessions</option>
            <option value="exit">Exit-intent sessions</option>
          </select>
        </div>
        <div class="bb-heatmap-field">
          <label for="bb-overlay-session">Specific session</label>
          <select id="bb-overlay-session" data-bb-overlay-session>
            <option value="">All sessions in selected scope</option>
          </select>
        </div>
      </div>
      <div class="bb-heatmap-overlay-trail" data-bb-overlay-trail hidden></div>
      <div class="bb-heatmap-overlay-hint" data-bb-overlay-hint>Tip: switch to rage or high-intent sessions to see cleaner, more useful click patterns than the full crowd view.</div>
      <div class="bb-heatmap-overlay-actions">
        <button type="button" data-bb-overlay-refresh>Refresh</button>
        <button type="button" data-bb-overlay-close class="bb-heatmap-overlay-close">Close overlay</button>
        <a href="${escapeHtml(state.returnUrl || '/')}">Back to dashboard</a>
      </div>
    `;
    document.body.appendChild(ui);

    const statusEl = ui.querySelector('[data-bb-overlay-status]');
    const gridEl = ui.querySelector('[data-bb-overlay-grid]');
    const refreshBtn = ui.querySelector('[data-bb-overlay-refresh]');
    const closeBtn = ui.querySelector('[data-bb-overlay-close]');
    const kindSelect = ui.querySelector('[data-bb-overlay-kind]');
    const sessionSelect = ui.querySelector('[data-bb-overlay-session]');
    const trailEl = ui.querySelector('[data-bb-overlay-trail]');
    const hintEl = ui.querySelector('[data-bb-overlay-hint]');
    const ctx = canvas.getContext('2d');

    kindSelect.value = state.kind || 'all';

    let currentData = null;
    let overlaySessions = [];
    let occupiedRects = [];

    const authHeaders = () => ({
      Authorization: `Bearer ${state.token}`,
      'X-Admin-Token': state.token
    });

    function setStatus(text, isError) {
      statusEl.textContent = text;
      statusEl.style.color = isError ? '#fecaca' : 'rgba(255,255,255,.86)';
    }

    function resizeCanvas() {
      const size = docSize();
      root.style.height = `${size.height}px`;

      const dpr = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = Math.round(size.width * dpr);
      canvas.height = Math.round(size.height * dpr);
      canvas.style.width = `${size.width}px`;
      canvas.style.height = `${size.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function visibleBox(el) {
      const rect = el.getBoundingClientRect();
      return {
        left: (window.scrollX || 0) + rect.left,
        top: (window.scrollY || 0) + rect.top,
        width: rect.width,
        height: rect.height
      };
    }

    function scoreElementMatch(el, descriptor) {
      if (!(el instanceof Element)) return -1;

      let score = 0;
      const text = visibleLabel(el).toLowerCase();

      if (descriptor.href && descriptor.href !== '/') {
        try {
          if (el.matches(`a[href*="${escapeForSelector(descriptor.href)}"]`)) score += 18;
        } catch (_err) {}
      }

      if (descriptor.selector) {
        try {
          if (el.matches(descriptor.selector)) score += 10;
        } catch (_err) {}
      }

      if (descriptor.label) {
        const needle = descriptor.label.toLowerCase();
        if (text === needle) score += 9;
        else if (needle && text.includes(needle)) score += 6;
      }

      if (descriptor.context) {
        const card = el.closest('[data-product-handle], [data-handle], .product-card, .collection-card, [data-product-card]');
        const ctxText = card ? visibleLabel(card).toLowerCase() : '';
        if (ctxText && ctxText.includes(String(descriptor.context).toLowerCase())) score += 4;
      }

      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) score += 2;

      return score;
    }

    function locateTargetElement(target) {
      const descriptor = parseTargetDescriptor(target);
      let nodes = [];

      if (descriptor.href && descriptor.href !== '/') {
        try {
          nodes = nodes.concat(Array.from(document.querySelectorAll(`a[href*="${escapeForSelector(descriptor.href)}"]`)));
        } catch (_err) {}
      }

      if (descriptor.selector) {
        try {
          nodes = nodes.concat(Array.from(document.querySelectorAll(descriptor.selector)));
        } catch (_err) {}
      }

      if (!nodes.length && descriptor.context) {
        nodes = Array.from(document.querySelectorAll('[data-product-handle], [data-handle], .product-card, .collection-card, [data-product-card]'));
      }

      if (!nodes.length) {
        nodes = Array.from(document.querySelectorAll('a, button, summary, [role="button"], input, select, textarea, label, .button, .product-card, .collection-card, [data-product-handle], [data-handle], [data-product-card]'));
      }

      let best = null;
      let bestScore = -1;

      for (const node of nodes.slice(0, 220)) {
        const score = scoreElementMatch(node, descriptor);
        if (score > bestScore) {
          best = node;
          bestScore = score;
        }
      }

      return bestScore >= 4 ? best : null;
    }

    function intersects(a, b) {
      return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
    }

    function positionLabel(label, cx, cy) {
      const rect = label.getBoundingClientRect();
      const size = docSize();

      const candidates = [
        { left: cx + 18, top: cy - rect.height - 18 },
        { left: cx + 18, top: cy + 16 },
        { left: cx - rect.width - 18, top: cy - rect.height - 18 },
        { left: cx - rect.width - 18, top: cy + 16 }
      ];

      for (const candidate of candidates) {
        const box = {
          left: Math.max(8, Math.min(candidate.left, size.width - rect.width - 8)),
          top: Math.max(8, Math.min(candidate.top, size.height - rect.height - 8))
        };

        box.right = box.left + rect.width;
        box.bottom = box.top + rect.height;

        let tries = 0;
        while (occupiedRects.some((r) => intersects(r, box)) && tries < 8) {
          box.top = Math.min(size.height - rect.height - 8, box.top + rect.height + 10);
          box.bottom = box.top + rect.height;
          tries += 1;
        }

        if (!occupiedRects.some((r) => intersects(r, box))) {
          occupiedRects.push(box);
          label.style.left = `${box.left}px`;
          label.style.top = `${box.top}px`;
          return true;
        }
      }

      return false;
    }

    function renderSessionOptions() {
      const selected = state.sessionId || '';
      sessionSelect.innerHTML = '<option value="">All sessions in selected scope</option>';

      overlaySessions.forEach((session, index) => {
        const opt = document.createElement('option');
        opt.value = session.session_id;
        opt.textContent = `#${index + 1} · ${session.clicks} clicks · ${session.trail[0] || '/'}${session.rage_clicks ? ` · ${session.rage_clicks} rage` : ''}${session.intent_events ? ` · ${session.intent_events} intent` : ''}`;
        if (session.session_id === selected) opt.selected = true;
        sessionSelect.appendChild(opt);
      });

      if (selected && !overlaySessions.some((s) => s.session_id === selected)) {
        state.sessionId = '';
        sessionSelect.value = '';
      }
    }

    function renderTrail() {
      const chosen = overlaySessions.find((s) => s.session_id === state.sessionId);

      if (!chosen) {
        trailEl.hidden = true;
        trailEl.innerHTML = '';
        return;
      }

      trailEl.hidden = false;
      trailEl.innerHTML = `<strong>Selected session trail</strong><ol>${chosen.trail.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ol>`;
    }

    function drawTargets() {
      targetLayer.innerHTML = '';
      occupiedRects = [];

      if (!currentData || !Array.isArray(currentData.anchored_targets)) return;

      const max = Math.max(...currentData.anchored_targets.map((item) => Number(item.count) || 0), 1);
      const withEls = [];

      for (const item of currentData.anchored_targets.slice(0, 24)) {
        const el = locateTargetElement(item.target);
        if (!el) continue;
        withEls.push({ item, el });
      }

      withEls
        .sort((a, b) => (Number(b.item.count) || 0) - (Number(a.item.count) || 0))
        .forEach(({ item, el }, index) => {
          const box = visibleBox(el);
          const cx = box.left + box.width / 2;
          const cy = box.top + box.height / 2;
          const norm = (Number(item.count) || 0) / max;
          const radius = 24 + (42 * norm);

          const highlight = document.createElement('div');
          highlight.className = 'bb-heatmap-target-highlight';
          highlight.style.left = `${box.left - 4}px`;
          highlight.style.top = `${box.top - 4}px`;
          highlight.style.width = `${Math.max(14, box.width + 8)}px`;
          highlight.style.height = `${Math.max(14, box.height + 8)}px`;
          highlight.style.borderColor = colorFor(Math.max(norm, 0.25), 0.9);
          targetLayer.appendChild(highlight);

          const ring = document.createElement('div');
          ring.className = 'bb-heatmap-target-ring';
          ring.style.left = `${cx}px`;
          ring.style.top = `${cy}px`;
          ring.style.width = `${radius * 2}px`;
          ring.style.height = `${radius * 2}px`;
          ring.style.background = `radial-gradient(circle, ${colorFor(norm, 0.28)} 0%, rgba(255,0,0,0) 72%)`;
          targetLayer.appendChild(ring);

          const pin = document.createElement('div');
          pin.className = 'bb-heatmap-target-pin';
          pin.style.left = `${cx}px`;
          pin.style.top = `${cy}px`;
          pin.textContent = String(item.count || 0);
          targetLayer.appendChild(pin);

          if (index < 8) {
            const desc = parseTargetDescriptor(item.target);
            const label = document.createElement('div');
            label.className = 'bb-heatmap-target-label';
            label.innerHTML = `${escapeHtml(desc.label || desc.href || desc.selector || 'Target')}<small>${Number(item.count) || 0} clicks</small>`;
            label.style.left = '-9999px';
            label.style.top = '-9999px';
            targetLayer.appendChild(label);
            if (!positionLabel(label, cx, cy)) label.remove();
          }
        });
    }

    function drawCloud() {
      resizeCanvas();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawTargets();

      if (!currentData || !Array.isArray(currentData.points) || !currentData.points.length) return;

      const useCloud = (state.type || 'click') !== 'click' || !currentData.anchored_targets || !currentData.anchored_targets.length;
      if (!useCloud) return;

      const size = docSize();
      const max = Math.max(...currentData.points.map((item) => Number(item.count) || 0), 1);
      const opacity = Math.min(Math.max((Number(state.opacity || 55) / 100), 0.15), 0.95);

      currentData.points.forEach((point) => {
        const x = clamp01(Number(point.x)) * size.width;
        const y = clamp01(Number(point.y)) * size.height;
        const norm = (Number(point.count) || 0) / max;
        const radius = 12 + (42 * norm);
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

        gradient.addColorStop(0, colorFor(norm, opacity * 0.75));
        gradient.addColorStop(0.65, colorFor(norm * 0.65, opacity * 0.28));
        gradient.addColorStop(1, 'rgba(255,0,0,0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function renderOverlayGrid() {
      const chosen = overlaySessions.find((s) => s.session_id === state.sessionId);

      if (chosen) {
        gridEl.innerHTML = `
          <div class="bb-heatmap-overlay-card"><strong>${Number(chosen.clicks) || 0}</strong><p>Clicks in selected session</p></div>
          <div class="bb-heatmap-overlay-card"><strong>${(Number(chosen.rage_clicks) || 0) + (Number(chosen.dead_clicks) || 0)}</strong><p>Confusion signals in selected session</p></div>
          <div class="bb-heatmap-overlay-card"><strong>${(Number(chosen.intent_events) || 0) + (Number(chosen.cart_events) || 0)}</strong><p>High-intent actions in selected session</p></div>
          <div class="bb-heatmap-overlay-card"><strong>${chosen.trail.length}</strong><p>Pages in this journey</p></div>
        `;
        hintEl.textContent = 'You are viewing one session only. Use this to follow an individual rage or high-intent shopper path through the site.';
        return;
      }

      const sessionsCount = overlaySessions.length || (currentData ? currentData.sessions : 0);
      const highIntent = overlaySessions.filter((s) => (Number(s.intent_events) + Number(s.cart_events) + Number(s.action_events)) > 0).length;
      const confusion = overlaySessions.filter((s) => (Number(s.rage_clicks) + Number(s.dead_clicks)) > 0).length;

      gridEl.innerHTML = `
        <div class="bb-heatmap-overlay-card"><strong>${sessionsCount}</strong><p>Sessions in selected scope</p></div>
        <div class="bb-heatmap-overlay-card"><strong>${currentData ? Number(currentData.total_events) || 0 : 0}</strong><p>Events on this page for current scope</p></div>
        <div class="bb-heatmap-overlay-card"><strong>${confusion}</strong><p>Confused / rage sessions available</p></div>
        <div class="bb-heatmap-overlay-card"><strong>${highIntent}</strong><p>High-intent sessions available</p></div>
      `;

      const labels = {
        all: 'all sessions on this page',
        rage: 'rage/confusion sessions',
        high_intent: 'high-intent sessions',
        cart: 'cart-intent sessions',
        dead: 'dead-click sessions',
        exit: 'exit-intent sessions'
      };

      hintEl.textContent = `Showing ${labels[state.kind] || 'selected sessions'}. Pick a single session to trace where that visitor went.`;
    }

    async function loadSessions() {
      const url = new URL('/api/overlay-sessions', overlayEndpoint + '/');

      url.searchParams.set('project', state.projectKey);
      url.searchParams.set('path', normalizePath(location.pathname));
      url.searchParams.set('device', state.device || 'all');
      url.searchParams.set('days', state.days || '7');
      url.searchParams.set('kind', state.kind || 'all');
      url.searchParams.set('limit', '24');
      url.searchParams.set('token', state.token);

      const res = await fetch(url.toString(), {
        headers: authHeaders(),
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-store'
      });

      if (!res.ok) throw new Error((await res.text()) || 'Session list request failed');

      const data = await res.json();
      overlaySessions = Array.isArray(data.sessions) ? data.sessions : [];
      renderSessionOptions();
      renderTrail();
    }

    async function loadOverlay() {
      setStatus('Loading overlay intelligence…');

      try {
        await loadSessions();

        const heatUrl = new URL('/api/heatmap', overlayEndpoint + '/');
        heatUrl.searchParams.set('project', state.projectKey);
        heatUrl.searchParams.set('path', normalizePath(location.pathname));
        heatUrl.searchParams.set('type', state.type || 'click');
        heatUrl.searchParams.set('device', state.device || 'all');
        heatUrl.searchParams.set('days', state.days || '7');
        heatUrl.searchParams.set('bins', '80');
        heatUrl.searchParams.set('kind', state.kind || 'all');
        if (state.sessionId) heatUrl.searchParams.set('session_id', state.sessionId);
        heatUrl.searchParams.set('token', state.token);

        const heatRes = await fetch(heatUrl.toString(), {
          headers: authHeaders(),
          credentials: 'omit',
          mode: 'cors',
          cache: 'no-store'
        });

        if (!heatRes.ok) throw new Error((await heatRes.text()) || 'Heatmap request failed');

        currentData = await heatRes.json();

        drawCloud();
        renderOverlayGrid();

        const scopeText = state.sessionId ? 'selected session' : (state.kind === 'all' ? 'all sessions' : `${state.kind.replace('_', ' ')} sessions`);
        setStatus(currentData.total_events ? `Loaded ${currentData.total_events} events for ${location.pathname} (${scopeText}).` : `Overlay loaded, but no data exists yet for ${scopeText}.`);
      } catch (error) {
        currentData = null;
        overlaySessions = [];
        targetLayer.innerHTML = '';
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setStatus(error.message || 'Heatmap could not load.', true);
      }
    }

    function closeOverlay() {
      try {
        localStorage.removeItem(overlayStateKey);
      } catch (_err) {}

      window.location.href = removeOverlayParam(window.location.href);
    }

    refreshBtn.addEventListener('click', loadOverlay);
    closeBtn.addEventListener('click', closeOverlay);

    kindSelect.addEventListener('change', () => {
      state.kind = kindSelect.value || 'all';
      state.sessionId = '';
      sessionSelect.value = '';

      try {
        localStorage.setItem(overlayStateKey, JSON.stringify(state));
      } catch (_err) {}

      loadOverlay();
    });

    sessionSelect.addEventListener('change', () => {
      state.sessionId = sessionSelect.value || '';

      try {
        localStorage.setItem(overlayStateKey, JSON.stringify(state));
      } catch (_err) {}

      renderTrail();
      loadOverlay();
    });

    window.addEventListener('resize', drawCloud, { passive: true });

    let resizeTimer = null;
    window.addEventListener('scroll', () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(drawCloud, 60);
    }, { passive: true });

    loadOverlay();
  }

  initOverlayMode();

  if (!enabled || !endpointRoot || !projectKey) return;
  if (window.Shopify && window.Shopify.designMode) return;

  if (respectDnt) {
    const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
    if (dnt === '1' || dnt === 'yes') return;
  }

  if (Math.random() > sampleRate) return;

  const queue = [];
  const maxQueue = 36;
  const flushEveryMs = 4500;
  const scrollMilestonesFired = new Set();

  let flushTimer = null;
  let destroyed = false;
  let lastMoveAt = 0;
  let lastViewAt = 0;
  let lastClicks = [];
  let latestPointer = null;
  let lastSentHash = '';
  let lastTouchAt = 0;
  let lastExitIntentAt = 0;

  try {
    const prevPath = sessionStorage.getItem(lastPathKey);
    if (prevPath) sessionStorage.setItem(refPathKey, prevPath);
    sessionStorage.setItem(lastPathKey, normalizePath(location.pathname));
  } catch (_err) {}

  function queueEvent(type, opts = {}) {
    if (destroyed || !ALLOWED_EVENT_TYPES.has(type)) return;

    const size = docSize();

    queue.push({
      type,
      x: clamp01(opts.x),
      y: clamp01(opts.y),
      vw: Math.max(window.innerWidth || 0, 1),
      vh: Math.max(window.innerHeight || 0, 1),
      dw: Math.max(size.width || 0, 1),
      dh: Math.max(size.height || 0, 1),
      target: String(opts.target || '').slice(0, 180),
      rage: !!opts.rage,
      ts: Date.now()
    });

    if (queue.length >= maxQueue) flush();
    else if (!flushTimer) flushTimer = window.setTimeout(flush, flushEveryMs);
  }

  function hashPayload(body) {
    return `${body.page.path}|${body.events.length}|${body.events[0] ? body.events[0].ts : 0}|${body.events[body.events.length - 1] ? body.events[body.events.length - 1].ts : 0}`;
  }

  async function flush() {
    if (destroyed) return;

    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    if (!queue.length) return;

    const events = queue.splice(0, queue.length);
    const bodyObj = {
      key: projectKey,
      sessionId,
      page: pageMeta(),
      events
    };

    const payloadHash = hashPayload(bodyObj);
    if (payloadHash === lastSentHash) return;
    lastSentHash = payloadHash;

    try {
      const res = await fetch(collectEndpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyObj)
      });

      if (!res.ok) throw new Error(await res.text() || `Collect failed (${res.status})`);

      window[debugOkKey] = Date.now();
      window[debugErrKey] = '';
    } catch (error) {
      // Put events back once, so a temporary D1/Worker problem does not instantly lose data.
      if (!destroyed && queue.length < maxQueue * 2) {
        queue.unshift(...events.slice(-maxQueue));
      }

      window[debugErrKey] = String(error && error.message || error);

      if (!flushTimer) {
        flushTimer = window.setTimeout(flush, Math.min(flushEveryMs * 2, 12000));
      }
    }
  }

  function recordPoint(type, clientX, clientY, target, extra = {}) {
    const size = docSize();
    const docX = (window.scrollX || 0) + Number(clientX || 0);
    const docY = (window.scrollY || 0) + Number(clientY || 0);

    queueEvent(type, {
      x: docX / Math.max(size.width, 1),
      y: docY / Math.max(size.height, 1),
      target: summarizeTarget(target),
      rage: extra.rage
    });
  }

  function queueSemantic(type, el, extra = {}) {
    if (!(el instanceof Element)) return;
    if (shouldIgnoreTarget(el) && !isSearchTarget(el)) return;

    const rect = el.getBoundingClientRect ? el.getBoundingClientRect() : {
      left: window.innerWidth / 2,
      top: window.innerHeight / 2,
      width: 0,
      height: 0
    };

    const cx = rect.left + (rect.width || 0) / 2;
    const cy = rect.top + (rect.height || 0) / 2;

    recordPoint(type, cx, cy, el, extra);
  }

  function maybeTrackOrder() {
    try {
      const checkout = window.Shopify && window.Shopify.checkout;
      const orderEndpoint = endpointRoot ? `${endpointRoot}/api/order` : '';

      if (!checkout || !checkout.order_id || !orderEndpoint || !projectKey) return;

      const dedupeKey = `${orderSentKey}_${checkout.order_id}`;
      if (sessionStorage.getItem(dedupeKey)) return;
      sessionStorage.setItem(dedupeKey, '1');

      const lineItems = Array.isArray(checkout.line_items)
        ? checkout.line_items.slice(0, 50).map((item) => ({
          title: item.title || '',
          quantity: Number(item.quantity) || 1,
          price: Number(item.price || 0) / 100,
          variant_id: item.variant_id || ''
        }))
        : [];

      fetch(orderEndpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: projectKey,
          sessionId,
          orderId: String(checkout.order_id),
          orderValue: Number(checkout.total_price || 0) / 100,
          currency: checkout.currency || 'USD',
          lineItems,
          referringPath: normalizePath(sessionStorage.getItem(refPathKey) || '/'),
          createdAt: Date.now()
        })
      }).catch(() => {});
    } catch (_err) {}
  }

  maybeTrackOrder();

  document.addEventListener('click', (event) => {
    if (Date.now() - lastTouchAt < 700) return;
    if (shouldIgnoreTarget(event.target) && !isSearchTarget(event.target)) return;

    const now = Date.now();
    const point = { x: event.clientX, y: event.clientY, at: now };

    lastClicks = lastClicks.filter((item) => now - item.at < 1200);
    lastClicks.push(point);

    const rage = lastClicks.length >= 3 &&
      lastClicks.every((item) => Math.abs(item.x - point.x) < 30 && Math.abs(item.y - point.y) < 30);

    recordPoint('click', event.clientX, event.clientY, event.target, { rage });
  }, true);

  document.addEventListener('click', (event) => {
    if (Date.now() - lastTouchAt < 700) return;

    const actionable = nearestActionable(event.target);
    if (!(actionable instanceof Element)) return;
    if (shouldIgnoreTarget(actionable) && !isSearchTarget(actionable)) return;

    const summary = summarizeTarget(actionable).toLowerCase();

    if (!isInteractiveTarget(actionable)) {
      recordPoint('dead_click', event.clientX, event.clientY, actionable);
    }

    if (/(add to cart|buy now|checkout|request quote|rfq|finance|pre-order|preorder|purchase|order)/i.test(summary)) {
      recordPoint('cta_click', event.clientX, event.clientY, actionable);
    }

    if (/(gallery|thumbnail|image|media|zoom|photo|slide)/i.test(summary)) {
      recordPoint('gallery_view', event.clientX, event.clientY, actionable);
    }

    if (/(filter|sort)/i.test(summary)) {
      recordPoint('filter_change', event.clientX, event.clientY, actionable);
    }

    if (isSearchTarget(actionable)) {
      recordPoint('search', event.clientX, event.clientY, actionable);
    }
  }, true);

  document.addEventListener('touchend', (event) => {
    const touch = event.changedTouches && event.changedTouches[0];
    if (!touch) return;
    if (shouldIgnoreTarget(event.target) && !isSearchTarget(event.target)) return;

    lastTouchAt = Date.now();
    recordPoint('click', touch.clientX, touch.clientY, event.target);
  }, { passive: true, capture: true });

  const isFinePointer = window.matchMedia && window.matchMedia('(pointer:fine)').matches;

  if (isFinePointer) {
    document.addEventListener('pointermove', (event) => {
      latestPointer = { x: event.clientX, y: event.clientY, target: event.target };

      const now = Date.now();
      if (now - lastMoveAt < 650) return;

      lastMoveAt = now;

      if (shouldIgnoreTarget(event.target) && !isSearchTarget(event.target)) return;

      recordPoint('move', event.clientX, event.clientY, event.target);
    }, { passive: true });
  }

  window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;

    const now = Date.now();
    if (now - lastViewAt < 900) return;

    lastViewAt = now;

    if (isFinePointer && latestPointer && now - lastMoveAt < 2500) {
      if (!shouldIgnoreTarget(latestPointer.target)) {
        recordPoint('view', latestPointer.x, latestPointer.y, latestPointer.target);
        return;
      }
    }

    const x = Math.round((window.innerWidth || 0) / 2);
    const y = Math.round((window.innerHeight || 0) * 0.38);
    const target = document.elementFromPoint ? document.elementFromPoint(x, y) : null;

    if (shouldIgnoreTarget(target) && !isSearchTarget(target)) return;

    recordPoint('view', x, y, target);
  }, 1000);

  function queueScrollMilestone() {
    const d = document.documentElement;
    const maxScroll = Math.max((d.scrollHeight || 0) - (window.innerHeight || 0), 1);
    const pct = Math.round(((window.scrollY || 0) / maxScroll) * 100);
    const milestones = [25, 50, 75, 90];

    for (const mark of milestones) {
      if (pct >= mark && !scrollMilestonesFired.has(mark)) {
        scrollMilestonesFired.add(mark);
        queueEvent('scroll', {
          x: 0.5,
          y: clamp01(mark / 100),
          target: `scroll-depth|${mark}%||`
        });
      }
    }
  }

  window.addEventListener('scroll', queueScrollMilestone, { passive: true });

  document.addEventListener('change', (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (shouldIgnoreTarget(el) && !isSearchTarget(el)) return;

    const summary = summarizeTarget(el).toLowerCase();

    if (/(variant|option|swatch|size|color)/i.test(summary) || el.name === 'id' || /option/i.test(el.name || '')) {
      queueSemantic('variant_change', el);
    } else if (/(sort|filter)/i.test(summary)) {
      queueSemantic('filter_change', el);
    } else if (isSearchTarget(el)) {
      queueSemantic('search', el);
    }
  }, { passive: true, capture: true });

  document.addEventListener('mouseleave', (event) => {
    if ((event.clientY || 999) > 10) return;

    const now = Date.now();
    if (now - lastExitIntentAt < 30000) return;

    lastExitIntentAt = now;

    queueEvent('exit_intent', {
      x: clamp01((event.pageX || 0) / Math.max(docSize().width, 1)),
      y: 0.001,
      target: 'exit_intent|||top-edge'
    });
  }, true);

  [15000, 30000, 60000, 120000].forEach((ms) => {
    window.setTimeout(() => {
      if (document.visibilityState === 'visible') {
        queueEvent('time_on_page', {
          x: 0.5,
          y: 0.35,
          target: `time_on_page|${ms / 1000}s||`
        });
      }
    }, ms);
  });

  const nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  if (nativeFetch) {
    window.fetch = function wrappedFetch(input, init) {
      const requestUrl = typeof input === 'string' ? input : (input && input.url) || '';
      const plainUrl = requestUrl.split('?')[0];

      const isCartAdd = /\/cart\/add(\.js)?$/i.test(plainUrl);
      const isCartChange = /\/cart\/(change|update)(\.js)?$/i.test(plainUrl);
      const isSearch = /\/search(\/suggest\.json)?$/i.test(plainUrl);

      const promise = nativeFetch(input, init);

      promise.then((res) => {
        if (!res || !res.ok) return;

        if (isCartAdd) {
          res.clone().json().then((data) => {
            const title = data && (data.product_title || data.title || 'cart add');
            queueEvent('cart_add', {
              x: 0.82,
              y: 0.18,
              target: `cart_add|${String(title || '').slice(0, 72)}||`
            });
          }).catch(() => {
            queueEvent('cart_add', { x: 0.82, y: 0.18, target: 'cart_add|||' });
          });
        } else if (isCartChange) {
          res.clone().json().then((data) => {
            const qty = Number(data && data.quantity);
            queueEvent(qty === 0 ? 'cart_remove' : 'cart_change', {
              x: 0.82,
              y: 0.18,
              target: qty === 0 ? 'cart_remove|||' : 'cart_change|||'
            });
          }).catch(() => {
            queueEvent('cart_change', { x: 0.82, y: 0.18, target: 'cart_change|||' });
          });
        } else if (isSearch) {
          queueEvent('search', { x: 0.5, y: 0.1, target: 'search|||' });
        }
      }).catch(() => {});

      return promise;
    };
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';

    if (/\/cart\/add/i.test(action)) {
      queueSemantic('cart_add', form);
    } else if (/contact|newsletter|klaviyo|mailchimp/i.test(action) || form.querySelector('input[type="email"]')) {
      queueSemantic('form_submit', form);
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (shouldIgnoreTarget(el) && !isSearchTarget(el)) return;

    if (el.matches('form input, form textarea, form select')) {
      queueSemantic('form_start', el);
    }
  }, true);

  window.addEventListener('pagehide', () => {
    destroyed = true;
    flush();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });

  window.addEventListener('beforeunload', flush);
})();
